import React, { useState } from 'react';
import FormWrapper from '../../../../../../Components/Form/FormWrapper';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import DateField from '../../../../../../Components/Form/components/DateField';
import SelectField from '../../../../../../Components/Form/components/SelectField';
import { SelectValue } from '../../../../../../Components/Base/Select/Select';
import InputWrapper from '../../../../../../Components/Form/components/InputWrapper/InputWrapper';
import InputField from '../../../../../../Components/Form/components/InputField';
import SubmitButton from '../../../../../../Components/Button/SubmitButton';
import useRestApiClient from '../../../../../Base/Hooks/useRestApiClient';
import useTranslation from '../../../../../../Hooks/useTranslation';
import ConfirmationModal from '../../../../../Base/Components/Confirmation/ConfirmationModal';
import Loading from '../../../../../../Components/Loading';
import { checkIsDateSameOrAfter } from '../../../../../Events/Services/eventInterval.service';
import moment from 'moment';
import apiClient from '../../../../../../Services/ApiClient';
import useFilters from '../../../../../Base/Hooks/useFilters';
import { useAppContext } from '../../../../../../Services/AppContext';
import useApiClient from '../../../../../Base/Hooks/useApiClient';

export interface Props {
	onSuccess: () => void;
	isSuperAdmin?: boolean;
}

const EXPORT_TYPES_OPTIONS: SelectValue[] = [
	{
		label: 'component.invoiceExportRequest.formatOptions.PDF',
		value: 'PDF',
	},
	{
		label: 'component.invoiceExportRequest.formatOptions.ISDOC',
		value: 'ISDOC',
	},
	{
		label: 'component.invoiceExportRequest.formatOptions.POHODA',
		value: 'POHODA',
	},
];

const InvoiceExportRequest: React.FC<Props> = ({ onSuccess, isSuperAdmin }) => {
	const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const { t } = useTranslation();
	const { processResult } = useRestApiClient();
	const { handleDateValue } = useFilters();
	const { workplaceId } = useAppContext();
	const { displayError } = useApiClient();
	const validationSchema = yup.object({
		dateFrom: yup
			.date()
			.typeError(t('validation.invoiceExportRequest.dateFrom'))
			.required(t('validation.invoiceExportRequest.dateFrom')),
		dateTo: yup
			.date()
			.typeError(t('validation.invoiceExportRequest.dateTo'))
			.test(
				'is-greater',
				t('validation.invoiceExportRequest.dateDiff') ?? ' ',
				function () {
					const { dateFrom, dateTo } = this.parent;
					return checkIsDateSameOrAfter(dateFrom, dateTo);
				},
			)
			.required(t('validation.invoiceExportRequest.dateTo')),
		format: yup.object().shape({
			label: yup.string(),
			value: yup.string().required(t('validation.invoiceExportRequest.format')),
		}),
		email: yup
			.string()
			.email(t('validation.invoiceExportRequest.emailFormat'))
			.required(t('validation.invoiceExportRequest.email')),
	});
	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
	});

	const handleSubmit = async (values: any) => {
		try {
			const result = await apiClient.post('/invoicing/export', {
				dateFrom: handleDateValue(values.dateFrom),
				dateTo: handleDateValue(values.dateTo),
				format: values.format.value,
				workplaceId,
				isAdmin: isSuperAdmin,
				recipients: [values.email],
			});
			processResult(result, () => {
				setIsConfirmationOpen(true);
			});
		} catch (e) {
			displayError(e);
		}
	};

	const handleCloseConfirmation = () => {
		setIsConfirmationOpen(false);
		onSuccess();
	};

	if (isLoading) {
		return <Loading />;
	}

	return (
		<>
			<FormWrapper formMethods={formMethods}>
				<div className='row'>
					<div className='col-6'>
						<InputWrapper>
							<DateField
								name='dateFrom'
								label='component.invoiceExportRequest.dateFrom'
								maxDate={moment()}
								usePortal
								isRequired
							/>
						</InputWrapper>
					</div>
					<div className='col-6'>
						<InputWrapper>
							<DateField
								name='dateTo'
								label='component.invoiceExportRequest.dateTo'
								maxDate={moment()}
								usePortal
								isRequired
							/>
						</InputWrapper>
					</div>
				</div>
				<InputWrapper>
					<SelectField
						name='format'
						label='component.invoiceExportRequest.format'
						options={EXPORT_TYPES_OPTIONS}
						isRequired
					/>
				</InputWrapper>
				<InputWrapper>
					<InputField
						name='email'
						label='component.invoiceExportRequest.email'
						isRequired
					/>
				</InputWrapper>
				<div className='mt-3'>
					<SubmitButton
						onSubmit={handleSubmit}
						title='component.invoiceExportRequest.submitButton'
					/>
				</div>
			</FormWrapper>
			{isConfirmationOpen && (
				<ConfirmationModal
					title='component.invoiceExportRequest.confirmationTitle'
					text={t('component.invoiceExportRequest.confirmationText')}
					imageSource='/dist/icons/v2/check-green.svg'
					isOpen
					onClose={handleCloseConfirmation}
				/>
			)}
		</>
	);
};

export default InvoiceExportRequest;
